/* eslint-disable max-len */
import { CircularProgress, makeStyles } from '@material-ui/core';
import { SortOrder } from '@moderepo/mode-apis';
import { BaseListCompDataItem, BaseListCompField, capitalizeAll, capitalizeFirst, DataPoint, getLanguageByCode, TimeSpan }
    from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { Agent, Entity } from '@moderepo/mode-internal-apis';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import moment from 'moment';
import React, { CSSProperties, useMemo, useCallback, useContext, useEffect, useState } from 'react';
import { EntityUIComponentCollection, FetchEntityTimeSpanDataParams, filterMetricsByMetricsExclusionCustomSettings,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings, getAggregatedValueBetweenTimestamps, getAllMetricsAggregatedValues,
    getKPIMetricsFromMetricDefinition, isFetchEntityTimeRangeDataParams, isMetricsDefinitionAndMetricsExclusionCustomSettings,
    MetricsDefinitionsAndMetricsExclusionCustomSettings, useSelectTranslatedEntityClasses } from '..';
import { EntitiesRankingTable, EntitiesRankingTableProps, EntityRankInfo } from '../../../components/entities';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectAllEntityChildren, useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { getPreferredLanguage, isObjectLiteral, isValidSettingNumberValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentWithChildrenProps } from '../EntityDetailsLayouts';
import { isRootEntity, getMetricKPIStatus, KPIStatus, createFetchEntityMetricDataParamsGroup, getKPIMetricDefinitions,
    FetchEntityMetricDataParamsGroup } from '../utils';


const useStyle = makeStyles(() => {
    return {
        rankingTable: {
            minHeight: 300,
        },
    };
}, { name: 'EntitiesRankingTableUIComponent', index: 1 });


// Maximum number of KPI metric we can display in the ranking table
const MAX_KPI_METRICS = 10;


/**
 * The interface to store the metrics' data for all entities.
 *      {
 *          "demo_room": {
 *              "avg_co2": {
 *                  dataPoints: [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
 *                  status: 'good',
 *              },
 *              "uptime": {
 *                  dataPoints: [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
 *                  status: 'bad',
 *              }
 *          },
 *          // The metrics' data for entity ID 'sfo'
 *          "sfo": {
 *              "avg_co2": {
 *                  dataPoints: [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
 *                  status: 'bad',
 *              },
 *              "uptime": {
 *                  dataPoints: [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
 *                  status: bad',
 *              }
 *          },
 *          // The metrics' data for entity ID 'tyo'
 *          "tyo": {
 *              "avg_co2": {
 *                  dataPoints: [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
 *                  status: 'good',
 *              },
 *              "uptime": {
 *                  dataPoints: [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
 *                  status: 'good',
 *              }
 *          }
 *      }
 */
interface EntitiesKPIMetricsData {
    readonly [entityId: string]: {
        readonly [metricName: string]: {
            readonly dataPoints: readonly DataPoint<number>[] | undefined;
            readonly status: KPIStatus
        } | undefined;
    } | undefined;
}

/**
 * Structure for keeping track of the current sorted field and sort order.
 */
interface RankingTableSearchParams {
    readonly sortField: string;
    readonly sortOrder: SortOrder;
}



/**
 * Given 2 values, compare them and return a number < 0 if value1 < value2. Return a number > 0 if value1 > value2. Return 0 if they are the
 * same. If a value1 is undefined and value2 is NOT undefined treat value2 as greater than value1 and vise versa.
 */
const compareValues = (value1: number | undefined, value2: number | undefined): number => {
    if (value1 !== undefined && value2 !== undefined) {
        return value1 - value2;
    }
    if (value1 !== undefined) {
        return 1;
    }
    if (value2 !== undefined) {
        return -1;
    }
    return 0;
};


/**
 * Helper function to compare 2 data items' values
 */
const compareItems = (
    item1: BaseListCompDataItem<EntityRankInfo>, item2: BaseListCompDataItem<EntityRankInfo>,
    sortField: string, sortOrder: SortOrder,
): number => {
    const value1 = item1.actualValue.metricsValueAndStatus[sortField];
    const value2 = item2.actualValue.metricsValueAndStatus[sortField];
    const result = compareValues(value1?.value, value2?.value);
    if (sortOrder === SortOrder.DESC) {
        return result * -1;
    }
    return result;
};



/**
 * Create an return an array of sorted EntityRankInfo based on the specified sortField and sortOrder.
 * lastIndexOffset is used for specifying which data we take from the entitiesMetricsData. We fetch data for multiple timeSpan, 1 for the
 * current timeSpan and another for the previous timeSpan. So if we want to create ranking data for the CURRENT timeSpan then we should
 * pass value 0 for lastIndexOffset. What this mean is to use the LAST data point from the data. If we need to create ranking data for the
 * PREVIOUS timeStan then this 'lastIndexOffset' should be 1, to fetch data at index 1 from the last item.
 */
const createRankingData = (
    entities: readonly Entity[], entitiesMetricsData: EntitiesKPIMetricsData,
    sortField: string, sortOrder: SortOrder,
    startTime: number, endTime: number,
): readonly BaseListCompDataItem<EntityRankInfo>[] => {

    const entitiesRanking = entities.map((entity): BaseListCompDataItem<EntityRankInfo> => {
        const oneEntityMetricsData = entitiesMetricsData[entity.entityId];
        return {
            actualValue: {
                entity,
                rankChange           : 0,
                metricsValueAndStatus: oneEntityMetricsData ? Object.keys(oneEntityMetricsData).reduce((result, metricName) => {
                    // Get the value for the given metricName and timestamp and then add it to the result
                    const metricData = oneEntityMetricsData[metricName];
                    const value = getAggregatedValueBetweenTimestamps(metricData?.dataPoints, startTime, endTime);
                    return {
                        ...result,
                        [metricName]: value !== undefined
                            ? {
                                value,
                                status: metricData?.status,
                            }
                            : undefined,
                    };
                }, {}) : {},
            },
        };
    }).sort((item1: BaseListCompDataItem<EntityRankInfo>, item2: BaseListCompDataItem<EntityRankInfo>) => {
        return compareItems(item1, item2, sortField, sortOrder);
    });

    return entitiesRanking;
};


/**
 * This is the structure for EntitiesRankingTableUIComponent custom settings. The EntitiesRankingTableUIComponent can be customized
 * from entityClass's uiSettings. When specifying the components placement for a ranking table, the agent/developer can add customSettings to the
 * components placement like this
 *
 *      entityClass = {
 *          displaySettings: {
 *              uiSettings: {
 *                  layoutSettings: {
 *                      componentsPlacement: {
 *                          "area_1": [{
 *                              component: "rankingTableComp",
 *                              customSettings: {
 *                                  maxItems: 10,
 *                                  defaultMetricName: 'uptime'
 *                              }
 *                          }, "childrenKpiComponent"]
 *                      }
 *                  }
 *              }
 *          }
 *      }
 */
export interface EntitiesRankingTableUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings {

    // To use a custom title instead of the default title "Ranking". Note that if a custom title is used, the translation must be added
    // to the project's translationOverride
    readonly titleKey?: string | undefined;

    // The maximum number of items to display. The default is display all items if not specified
    readonly maxItems?: number | undefined;

    readonly rankChildrenOfClass?: string | undefined;

    // The natural ordering of each metrics. By default, we will order the metrics' value by DESC ordering which mean the metric with highest
    // value will be first. However, some metric might have the opposite order where the smaller the value the better. So for those metrics
    // that have opposite ordering, they need to be specified here. If none of the metrics have this ordering, this can be left empty
    readonly defaultMetricsOrdering?: {
        readonly [metricName: string]: SortOrder | undefined;
    } | undefined;

    // The name of the metric to rank the entity by default. If not specified, the first metric found in the entity class will be used
    readonly defaultMetricName?: string | undefined;

    // The set of metrics to show in the ranking table. An Entity Class can have many KPI metrics but maybe some of them shouldn't be
    // used for ranking. If there are one or more metrics that shouldn't be used for ranking, then we need to specify which metric
    // can be used for ranking. By default, we will show all metrics. Unless this metrics array is provided then we will only show the
    // metrics specified in this array and exclude all other
    readonly rankableMetricNames?: readonly string[] | undefined;
}


/**
 * Type guard to make sure an EntitiesRankingTableUIComponentCustomSettings is valid. Since this config is set by agent/developer
 * manually therefore we need to make sure the config is valid before using it.
 */
export const isEntitiesRankingTableUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntitiesRankingTableUIComponentCustomSettings => {
    const settings = obj as EntitiesRankingTableUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined of a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.rankChildrenOfClass)) {
        return errorLogger?.logError({
            message: '"rankChildrenOfClass" must be a non-empty string.',
            value  : settings.rankChildrenOfClass,
            path   : [...configPath, 'rankChildrenOfClass'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.maxItems, false, false, 1, 100)) {
        return errorLogger?.logError({
            message: '"maxItems" must be undefined or an integer between 1 and 100 inclusive.',
            value  : settings.maxItems,
            path   : [...configPath, 'maxItems'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultMetricName)) {
        return errorLogger?.logError({
            message: '"defaultMetricName" must be undefined or a non-empty string.',
            value  : settings.defaultMetricName,
            path   : [...configPath, 'defaultMetricName'],
        }) ?? false;
    }

    // rankableMetricNames is optional but if it is provided, if must be an array of strings
    if (settings.rankableMetricNames !== undefined) {
        if (!(settings.rankableMetricNames instanceof Array) || settings.rankableMetricNames.length === 0) {
            return errorLogger?.logError({
                message: '"rankableMetricNames" must be undefined or a non-empty array of strings.',
                value  : settings.rankableMetricNames,
                path   : [...configPath, 'rankableMetricNames'],
            }) ?? false;
        }

        // If some metricName in "rankableMetricNames" is NOT non-empty strings then return false
        if (!settings.rankableMetricNames.every((metricName, index) => {
            if (!isValidSettingStringValue(metricName, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "rankableMetricNames" array must be non-empty strings.',
                    value  : metricName,
                    path   : [...configPath, 'rankableMetricNames', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // defaultMetricsOrdering is optional but if it is provided, if must be a map of metricName => SortOrder
    if (settings.defaultMetricsOrdering !== undefined) {
        if (!isObjectLiteral(settings.defaultMetricsOrdering)) {
            return errorLogger?.logError({
                message: '"defaultMetricsOrdering" must be undefined or an object.',
                value  : settings.defaultMetricsOrdering,
                path   : [...configPath, 'defaultMetricsOrdering'],
            }) ?? false;
        }

        // If NOT every values in the "defaultMetricsOrdering" IS either ASC or DESC then return false
        if (!Object.entries(settings.defaultMetricsOrdering).every(([metricName, order]) => {
            if (order !== SortOrder.ASC && order !== SortOrder.DESC) {
                return errorLogger?.logError({
                    message: 'Every values in "defaultMetricsOrdering" must be "asc" or "desc".',
                    value  : order,
                    path   : [...configPath, 'defaultMetricsOrdering', metricName],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // Because this component's settings interface extends MetricsDefinitionAndMetricsExclusionCustomSettings, we can call
    // "isMetricsDefinitionAndMetricsExclusionCustomSettings" to validate it
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};


export interface EntitiesRankingTableUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly customSettings?: EntitiesRankingTableUIComponentCustomSettings | undefined;
}


/**
 * This is an EntityUIComponent that can display ranking info for a given entity comparing to its' siblings or display the ranking
 * info of the given entity's children
 */
export const EntitiesRankingTableUIComponent: React.FC<EntitiesRankingTableUIComponentProps> = (props: EntitiesRankingTableUIComponentProps) => {

    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = useSelectProject(props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;

    // Get current selected language in the uiState
    const selectedLanguage = getLanguageByCode(
        SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || DEFAULT_LANGUAGE;

    // Get the agent's preferred language and figure out the language that is being used in the UI based on the agent's preferred language and the
    // current language in the uiState. If the agent is logged in and has preferred language set than use the agent's preferred language.
    // Else use the current selected language in the UI state.
    const currentLanguage = loggedInAgent
        ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE)
        : selectedLanguage;

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // For now, this component will only support FetchEntityTimeSpanDataParams. So if the fetchDataParams is another type,
    // we will try to create FetchEntityTimeSpanDataParams using whatever value we have from the fetchDataParams.
    // This component is used for displaying KPI metrics so we might not need to support other fetchDataParams
    const fetchDataParams = useMemo((): FetchEntityTimeSpanDataParams => {
        if (isFetchEntityTimeRangeDataParams(props.fetchDataParams)) {
            return {
                timestamps: [props.fetchDataParams.startTime],
                timeSpan  : TimeSpan.DAY,
            };
        }
        return props.fetchDataParams ? props.fetchDataParams : {
            timeSpan  : TimeSpan.DAY,
            timestamps: [Date.now()],
        };
    }, [props.fetchDataParams]);


    /**
     * The local state to keep track of the ranking table's sorted column and the sort order. Initially, we don't know the sortField yet because
     * we don't have the list of KPI metrics
     */
    const [rankingSortStatus, setRankingSortStatus] = useState<RankingTableSearchParams | undefined>();


    // Get all entityClasses
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Get the data for the selected entity
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    /**
     * Find the entityClass object that the entity class we want to target. By default, we will target entity class of the selected entity
     * because the default is comparing the selected entity's siblings. However, if "props.customSettings.rankChildrenOfClass" is specified,
     * we will be comparing children of the selected entity therefore, we will use the "props.customSettings.rankChildrenOfClass" as the
     * targetEntityClass instead.
     */
    const targetEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            const targetEntityClassName = props.customSettings?.rankChildrenOfClass ?? selectedEntity.entityClass;
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === targetEntityClassName;
            });
        }
        return undefined;
    }, [entityClasses, props.customSettings?.rankChildrenOfClass, selectedEntity]);

    // We need to make API call to fetch all the entities of the same class. We need to pass this filter to the API so we will create this
    // filter and use useMemo to cache it so that the useSelectAllEntityChildren won't get called multiple times
    const getEntitiesFilters = useMemo(() => {
        return {
            entityClasses: targetEntityClass?.entityClass,
        };
    }, [targetEntityClass]);

    /**
     * Get the parent entity id of the entities we want to display.
     *
     * When we fetch entities to display in the ranking table, we need to fetch the entities for a given parent. Since by default, we compare siblings
     * entities therefore, we use the selected's entity parent as the parent entity. HOWEVER, if we need to compare the selected entity's children
     * then the parent entity will be the selected entity itself.
     */
    const parentEntityId = useMemo(() => {
        // If we are comparing the selected entity children when use the selected entity as the parent.
        if (props.customSettings?.rankChildrenOfClass) {
            return selectedEntity?.entityId;
        }

        // If we are comparing siblings then use the selected entity's parent as the parent entity.
        return selectedEntity && !isRootEntity(selectedEntity) ? selectedEntity.parentId : undefined;
    }, [props.customSettings?.rankChildrenOfClass, selectedEntity]);

    // Now call the API to fetch entities to be shown
    const selectedEntitiesToDisplay = useSelectAllEntityChildren(
        props.projectId, parentEntityId, getEntitiesFilters,
    );



    /**
     * Once we have the targetEntityClass, we need to get all the KPI metric definitions from the entity class, the metric definition that has
     * at least 1 KPI numeric metric. All the metrics definition that DOES NOT have at least 1 KPI metrics will be excluded
     */
    const kpiMetricDefinitions = useMemo(() => {
        const kpiMetricsDef = getKPIMetricDefinitions(targetEntityClass);

        // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
        const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
            kpiMetricsDef ?? [], props.customSettings,
        );

        return filteredMetricsDef;
    }, [props.customSettings, targetEntityClass]);



    /**
     * Get the list of KPI metrics from the list of kpiMetricDefinitions
     */
    const kpiMetrics = useMemo(() => {
        if (kpiMetricDefinitions) {
            const kpiMetrics = kpiMetricDefinitions.map((metricDef) => {
                // Get all the KPI metrics for the metric definition
                const metrics = getKPIMetricsFromMetricDefinition(metricDef);

                // If "metricsExclusionSettings" is provided, filter out metrics based on "metricsExclusionSettings"
                const filteredKpiMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId, metrics, props.customSettings, true,
                );
                return filteredKpiMetrics;
            }).flat();

            // If there is a defined list of rankable metric names then we need to filter out the metrics that are not in this list
            if (kpiMetrics && props.customSettings?.rankableMetricNames && props.customSettings.rankableMetricNames.length > 0) {
                return kpiMetrics.filter((metric) => {
                    return props.customSettings?.rankableMetricNames?.includes(metric.name);
                }).slice(0, MAX_KPI_METRICS);
            }

            return kpiMetrics?.slice(0, MAX_KPI_METRICS);
        }
        return undefined;
    }, [kpiMetricDefinitions, props.customSettings]);



    /**
     * This useEffect will take care of initializing the rankingSortStatus once we have the list of kpiMetricsInfo
     */
    useEffect(() => {
        if (kpiMetrics && kpiMetrics.length > 0) {
            // We have some kpiMetrics and we need to pick one of the metrics as the metrics that the entities are ranked by.
            // We will pick the first one UNLESS a 'defaultMetricName' is set
            const defaultSortMetric = kpiMetrics.find((metric) => {
                return metric.name === props.customSettings?.defaultMetricName;
            }) ?? kpiMetrics[0];

            setRankingSortStatus({
                sortField: defaultSortMetric.name,
                sortOrder: props.customSettings?.defaultMetricsOrdering?.[defaultSortMetric.name] ?? SortOrder.DESC,
            });
        }
    }, [kpiMetrics, props.customSettings]);



    /**
     * To compute the ranking, we will fetch KPI metrics' data for each sibling entities and store them in this structure
     *      allEntitiesKPIMetricsData = {
     *          // The metrics' data for entity ID 'demo_room'
     *          "demo_room": {
     *              "avg_co2": [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
     *              "uptime": [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
     *          },
     *          // The metrics' data for entity ID 'sfo'
     *          "sfo": {
     *              "avg_co2": [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
     *              "uptime": [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
     *          },
     *          // The metrics' data for entity ID 'tyo'
     *          "tyo": {
     *              "avg_co2": [{date:"Oct 1", value: 10}, {date:"Oct 2", value: 20}],
     *              "uptime": [{date:"Oct 1", value: 1000}, {date:"Oct 2", value: 1500}],
     *          }
     *      }
     */
    const [allEntitiesKPIMetricsData, setEntitiesKPIMetricsData] = useState<EntitiesKPIMetricsData>();

    const fetchAllEntitiesKPIMetricsData = useCallback(async (): Promise<EntitiesKPIMetricsData | undefined> => {
        if (
            selectedEntitiesToDisplay && selectedEntitiesToDisplay.length > 0
            && kpiMetricDefinitions && kpiMetricDefinitions.length > 0
        ) {
            const timestamp = fetchDataParams.timestamps[0];
            const { timeSpan } = fetchDataParams;

            const fetchActions: Promise<readonly DataPoint<number>[][] | undefined>[] = [];
            const multiEntitiesFetchDataGroupsInfo: FetchEntityMetricDataParamsGroup[] = [];

            // For each entity, create a fetch the entity's KPI's metric data action for the selected timeSpan. We will not make the call right
            // away. We will collect all the API call and dispatch them all at the same time later.
            selectedEntitiesToDisplay.forEach((entity) => {

                // Call 'createFetchEntityMetricDataParams' to create the information we need to fetch metric data. This DOES NOT fetch data,
                // it only return array of object which contains information we need for making API calls later.
                const multipleFetchDataParamsGroups = createFetchEntityMetricDataParamsGroup(
                    kpiMetricDefinitions, entity, true, props.customSettings,
                );

                multipleFetchDataParamsGroups.forEach((group) => {
                    // Add the fetch action to the array
                    fetchActions.push(getAllMetricsAggregatedValues(
                        group,
                        moment(timestamp).subtract(1, timeSpan).startOf(timeSpan).valueOf(),    // start from 1 timeSpan ago
                        moment(timestamp).endOf(timeSpan).valueOf(),                            // stop at the END current timeSpan
                        timeSpan,
                    ));

                    // Add group info to the multiEntitiesFetchDataGroupsInfo array so that they have the same ordering as the fetchActions array.
                    // We will need these data later When we have get the response from the API call
                    multiEntitiesFetchDataGroupsInfo.push(group);
                });
            });

            const newEntitiesKpiMetricData: Partial<EntitiesKPIMetricsData> = {};

            // Make all the API calls in parallel and wait for the response
            await Promise.allSettled(fetchActions).then((allResponses) => {

                allResponses.forEach((oneResponse, index) => {

                    // The get the fetchDataInfo at the same index as the response, we will need the entity and metric info.
                    const group = multiEntitiesFetchDataGroupsInfo[index];

                    // Get metrics' data for 1 entity. If it does not exist, create a new one.
                    const oneEntityMetricsData: Partial<EntitiesKPIMetricsData['']> = newEntitiesKpiMetricData[group.entity.entityId] ?? {};

                    // Set oneEntityMetricsData back to the newEntitiesKpiMetricData incase oneEntityMetricsData is new
                    newEntitiesKpiMetricData[group.entity.entityId] = oneEntityMetricsData;

                    if (oneResponse.status === 'fulfilled') {
                        // NOTE: Each response.value is a double array of data points, 1 for each metric in the group
                        // Therefore we need to oop through the array of metrics in the group and setup data for each metric
                        const multiMetricsDataPoints = oneResponse.value;

                        group.metrics.forEach((metric, metricIndex) => {

                            const metricDataPoints = multiMetricsDataPoints?.[metricIndex];

                            // Find the CURRENT value from the data point array, this is the value we will rank the metrics by
                            const currentValue = getAggregatedValueBetweenTimestamps(
                                metricDataPoints, moment(timestamp).startOf(timeSpan).valueOf(), moment(timestamp).endOf(timeSpan).valueOf(),
                            );

                            oneEntityMetricsData[metric.name] = {
                                dataPoints: metricDataPoints,
                                status    : getMetricKPIStatus(currentValue, metric, timeSpan),
                            };
                        });
                    } else {
                        // API call failed. We still need to set up the data for the current entity, we just have to set the data
                        // for each metric to undefined
                        group.metrics.forEach((metric) => {
                            oneEntityMetricsData[metric.name] = undefined;
                        });
                    }
                });
            }).catch(() => {
            });

            return newEntitiesKpiMetricData;
        }

        return undefined;
    }, [selectedEntitiesToDisplay, kpiMetricDefinitions, fetchDataParams, props.customSettings]);


    /**
     * This useEffect will automatically call fetch entities' metrics' data
     */
    useEffect(() => {
        fetchAllEntitiesKPIMetricsData().then((result) => {
            // Update the local state with the new entities' metrics' data
            setEntitiesKPIMetricsData(result);
        });
    }, [fetchAllEntitiesKPIMetricsData]);


    /**
     * Create ranking table data. Once we have all the entities' metrics' data, we can generate this array of data to be passed to the
     * ranking table for display. This array will be sorted based on the current rankingSortStatus therefore it will be called each time
     * the user changed the sort order or choose a different column to be sorted.
     */
    const entitiesRankingData = useMemo((): EntitiesRankingTableProps['listData'] => {
        if (rankingSortStatus && selectedEntitiesToDisplay && allEntitiesKPIMetricsData) {

            const { sortField } = rankingSortStatus;
            const { sortOrder } = rankingSortStatus;
            const timestamp = fetchDataParams.timestamps[0];
            const { timeSpan } = fetchDataParams;


            // Create the ranking for the entities' based on the data of the PREVIOUS timeSpan. E.g. if the user is looking at 1 WEEK data
            // for this week then this will return the ranking of the entities for LAST WEEK
            const sortedPreviousRanking = createRankingData(
                selectedEntitiesToDisplay, allEntitiesKPIMetricsData, sortField, sortOrder,
                moment(timestamp).subtract(1, timeSpan).startOf(timeSpan).valueOf(),
                moment(timestamp).subtract(1, timeSpan).endOf(timeSpan).valueOf(),
            );

            // Create the ranking for the entities' based on the data of the CURRENT timeSpan. E.g. if the user is looking at 1 WEEK data
            // for this week then this will return the ranking of the entities for THIS WEEK
            const sortedCurrentRanking = createRankingData(
                selectedEntitiesToDisplay, allEntitiesKPIMetricsData, sortField, sortOrder,
                moment(timestamp).startOf(timeSpan).valueOf(),
                moment(timestamp).endOf(timeSpan).valueOf(),
            );


            // Now compare the currentRanking with previousRanking to compute the changes in the rank for each entity
            const finalRanking = sortedCurrentRanking.map((item, currentRankNumber): BaseListCompDataItem<EntityRankInfo> => {
                const { entity } = item.actualValue;

                // Find the index of the entity in the previousRanking array. The index will be the entity's previous ranking number
                const previousRankNumber = sortedPreviousRanking.findIndex((rank) => {
                    return rank.actualValue.entity.entityId === entity.entityId;
                });

                // 'rankChange' is read only therefore we need to return a copy of the item with the new rankChange
                return {
                    ...item,
                    actualValue: {
                        ...item.actualValue,
                        rankChange           : currentRankNumber - previousRankNumber,
                        metricsValueAndStatus: Object.keys(item.actualValue.metricsValueAndStatus).reduce((result, metricName) => {
                            // Round the metrics' value. The reason we didn't do it earlier is because we need the full value for accurate sorting
                            // Now we are done sorting therefore we can round the values for better display
                            const metricValueAndStatus = item.actualValue.metricsValueAndStatus[metricName];
                            return {
                                ...result,
                                [metricName]: metricValueAndStatus ? {
                                    ...metricValueAndStatus,
                                    value: trans('{{value, number-format}}', { value: metricValueAndStatus.value }),
                                } : undefined,
                            };
                        }, {}),
                    },
                };
            });

            if (props.customSettings?.maxItems !== undefined && props.customSettings.maxItems > 0) {
                // If there is a maxItems set, trim the array of ranking and only leave up to 'maxItems' items.
                return finalRanking.splice(0, props.customSettings.maxItems);
            }

            // No maxItems specified, return all items
            return finalRanking;
        }
        return [];
    }, [allEntitiesKPIMetricsData, props.customSettings, fetchDataParams, rankingSortStatus, selectedEntitiesToDisplay, trans]);


    // Get the formatted display date for the ranking to be displayed on the ranking table's header
    const rankingDate = useMemo((): string | undefined => {
        const timestamp = fetchDataParams.timestamps[0];
        const { timeSpan } = fetchDataParams;
        const dateFormatOption = ((): Intl.DateTimeFormatOptions => {
            if (timeSpan === TimeSpan.YEAR) {
                return { year: 'numeric' };
            }
            if (timeSpan === TimeSpan.MONTH) {
                return { year: 'numeric', month: 'long' };
            }
            if (timeSpan === TimeSpan.WEEK || timeSpan === TimeSpan.DAY) {
                return { year: 'numeric', month: 'long', day: '2-digit' };
            }
            return { year: 'numeric', month: 'long', day: '2-digit', hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' };
        })();
        return new Intl.DateTimeFormat(currentLanguage.locale2, dateFormatOption).format(timestamp);
    }, [currentLanguage, fetchDataParams]);



    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        setRankingSortStatus((currentSortInfo) => {
            return produce(currentSortInfo, (draft: Draft<RankingTableSearchParams>) => {
                const fieldName = field.dataItemProp;
                if (draft.sortField === fieldName) {
                    // this column is currently being sorted, toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else if (fieldName) {
                    // This column is not the current column being sorted, change it to this column and also set the sort order back to
                    // DESC order or customized default ordering for the selected field
                    draft.sortField = fieldName;
                    draft.sortOrder = props.customSettings?.defaultMetricsOrdering?.[fieldName] ?? SortOrder.DESC;
                }
            });
        });
    }, [props.customSettings]);


    /**
     * Create the table columns for the ranking table. The table will have a rank # column, entity name column, the rank change column,
     * and then 1 column for each KPI metric. This list of metric is dynamic and will be based on the targetEntityClass therefore we
     * need to wait until we have the entity class before we can continue
     */
    const tableFieldSettings = useMemo((): EntitiesRankingTableProps['fieldsSettings'] => {
        if (kpiMetrics && kpiMetrics.length > 0 && selectedEntitiesToDisplay && rankingSortStatus) {

            // For each KPI metrics, add a field/column for the table
            const kpiMetricFields: EntitiesRankingTableProps['fieldsSettings']['fields'] = {};
            kpiMetrics.forEach((metric) => {
                kpiMetricFields[metric.name] = {
                    label       : metric.displayName ?? metric.name,
                    dataItemProp: metric.name,
                    unit        : metric.unit,
                    sortable    : selectedEntitiesToDisplay.length > 1,        // If there is only 1 entity then don't need to enable sorting
                };
            });

            const allFields: EntitiesRankingTableProps['fieldsSettings']['fields'] = {
                preview: {
                    label: '',
                },
                rank: {
                    label   : capitalizeAll(trans('label.rank_number')),
                    sortable: false,
                },
                name: {
                    label: capitalizeAll(trans('label.name')),
                },
                ...kpiMetricFields,     // Insert KPI fields in the middle after 'Name' and before 'Rank Change' columns
                rankChange: {
                    label: '',
                },
            };


            return {
                fields            : allFields,
                currentSortedField: rankingSortStatus.sortField,
                currentSortedOrder: rankingSortStatus.sortOrder,
                onSortByField,
            };
        }

        // We haven't fetch data yet or there isn't any KPI metrics so don't create any column for the table
        return {
            fields: {},
        };
    }, [kpiMetrics, onSortByField, rankingSortStatus, selectedEntitiesToDisplay, trans]);



    /**
     * On Preview button clicked from the ranking table, dispatch an "onShowEntityPreview" event to the parent container
     */
    const onPreviewItemClicked = useCallback((entityRank: EntityRankInfo) => {
        if (props.onShowEntityPreview) {
            props.onShowEntityPreview(entityRank.entity);
        }
    }, [props]);


    return (
        <>
            {project && selectedEntity && tableFieldSettings && entitiesRankingData ? (
                <>
                    {kpiMetrics && kpiMetrics?.length > 0 && (
                        <EntitiesRankingTable
                            className={clsx(props.className, classes.rankingTable)}
                            style={props.style}
                            icon="bar_chart"
                            title={props.customSettings?.titleKey
                                ? trans(props.customSettings.titleKey)
                                : capitalizeFirst(trans('entities.entities_ranking.panel.title'))}
                            fieldsSettings={tableFieldSettings}
                            listData={entitiesRankingData}
                            date={rankingDate}
                            currentEntity={selectedEntity}
                            maxItems={props.customSettings?.maxItems}
                            onPreviewItemClicked={onPreviewItemClicked}
                            onListItemSelected={(entityRank) => {
                                if (props.onShowEntityDetails) {
                                    props.onShowEntityDetails(entityRank.entity);
                                }
                            }}
                        />
                    )}
                </>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntitiesRankingTableUIComponent', EntitiesRankingTableUIComponent);


// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Ranking_Table_Comp',
    {
        componentFunc          : EntitiesRankingTableUIComponent,
        customSettingsValidator: isEntitiesRankingTableUIComponentCustomSettings,
    },
);
